/**
 * Cluster 1 · Essential information
 *
 * Contains: Identity section (top), plus the second block —
 * Curriculum & content, Teaching & learning, Accreditation & recognition.
 *
 * Extracted verbatim from DegreeDetail.jsx (Feb 2026 refactor). Two
 * ClusterMember blocks are emitted because that's how the parent
 * historically arranged them; CSS flex `order` on the shared parent
 * flex container is what visually collapses them into one cluster.
 */
import { ClusterMember } from "@/components/app/degree/ClusterLayout";
import { Section, DataRow, Ev } from "@/components/app/degree/DegreeDetailBits";
import { CourseMetaChips, NssThemesBlock } from "@/components/app/degree/HesaPhase3Bits";
import { LocationBlock } from "@/components/app/degree/HesaPhase3Bits";
import { AIInterpretationNote } from "@/components/app/AIInterpretationCard";
import TeachingLearningCluster from "@/components/app/degree/TeachingLearningCluster";
import HesaCurriculumSnapshot from "@/components/app/degree/HesaCurriculumSnapshot";
import { HesaAccreditations, TefBadge } from "@/components/app/degree/HesaPhase1Bits";
import { UsInstitutionSignals } from "@/components/app/degree/UsInstitutionSignals";
import { Card } from "@/components/ui/card";
import { ExternalLink } from "lucide-react";

export default function Cluster1Essential({ ctx }) {
  const { d, uni, hs, ai, isHesa, isDaad, isScorecard, metaAwarding } = ctx;
  return (
    <>
      <ClusterMember cluster={1}>
      <Section id="identity" title="Degree identity">
        <div className="grid md:grid-cols-2 gap-x-8">
          <DataRow k="Subject area" v={d.subject_area} />
          <DataRow
            k="Tags"
            v={
              (d.tags && d.tags.length ? d.tags : (isHesa ? [
                hs.course_meta?.honours && "Honours",
                hs.course_meta?.sandwich_placement && "Sandwich placement",
                hs.course_meta?.year_abroad_available && "Year abroad",
                hs.course_meta?.distance_learning && "Distance learning",
                hs.course_meta?.nhs_funded && "NHS-funded",
              ] : [])).filter(Boolean).join(" · ") || "—"
            }
          />
          <DataRow k="Level" v={d.level} />
          <DataRow
            k="Mode"
            v={
              d.mode
              || (hs.course_meta?.distance_learning ? "Distance learning" : null)
              || (hs.leo_sec?.kismode === "01" ? "Full-time"
                : hs.leo_sec?.kismode === "02" ? "Part-time"
                : hs.leo_sec?.kismode === "03" ? "Both full & part-time"
                : hs.leo_sec?.kismode === "04" ? "Sandwich" : "—")
            }
          />
          <DataRow
            k="Duration"
            v={d.duration_years ? `${d.duration_years} year${d.duration_years === 1 ? "" : "s"}${
              hs.course_meta?.num_stages && hs.course_meta.num_stages !== d.duration_years
                ? ` · ${hs.course_meta.num_stages} stages` : ""
            }` : "—"}
          />
          <DataRow k="Intake" v={d.intake || (isHesa ? "September" : "—")} />
          <DataRow k="Awarding body" v={metaAwarding || "—"} />
          <DataRow
            k="Currently recruiting"
            v={d.currently_recruiting === false ? "No" : (d.currently_recruiting === true || isHesa) ? "Yes" : "—"}
          />
          {d.language_of_instruction && (
            <DataRow
              k="Language of instruction"
              v={{
                es:    "Spanish (grado en español)",
                "es+ca": "Spanish + Catalan",
                "es+eu": "Spanish + Basque",
                en:    "English (fully English-taught)",
                "en+es": "English + Spanish (bilingual track)",
              }[d.language_of_instruction] || d.language_of_instruction}
            />
          )}
          {d.autonomous_community && (
            <DataRow k="Autonomous community" v={d.autonomous_community} />
          )}
          {d.region && !d.autonomous_community && (
            <DataRow k="Region" v={d.region} />
          )}
          {d.mur_stats?.classe_codice && (
            <DataRow k="MUR classe (Italy)" v={d.mur_stats.classe_codice} />
          )}
          {d.official_title_local && d.official_title_local !== d.official_title && (
            <DataRow k="Local title" v={d.official_title_local} />
          )}
        </div>
        <CourseMetaChips meta={d.hesa_stats?.course_meta} />
        <LocationBlock inst={d.hesa_stats?.institution} />
      </Section>
      </ClusterMember>

      {/* Feb 2026 · Teaching & Learning cluster — was mounted here at the
          top of Cluster 1; the operator asked (Feb 19, 2026) to move the
          four charts (Assessment Mix donut, TEF Gold badges, NSS Student
          Voice bars, Weekly Rhythm contact hours) into the Section
          id="teaching" so they render inside their own semantic header.
          The cluster now lives inside that Section further down; leaving
          this comment as a breadcrumb for future readers. */}

      <ClusterMember cluster={1}>
      <Section id="curriculum" title="Curriculum & content">
        <AIInterpretationNote
          interpretation={ai.interpretation}
          sectionKey="curriculum"
          degreeId={d.id}
          dataAvailable={
            !!(d.curriculum?.compulsory_modules ||
               d.curriculum?.specialisms?.length ||
               d.curriculum?.weekly_workload_hours ||
               d.curriculum?.assessment_mix ||
               hs.nss_avg != null ||
               hs.course_meta?.num_stages)
          }
        />
        {d.curriculum?.compulsory_modules ? (
          <>
            {Object.entries(d.curriculum.compulsory_modules).map(([yr, mods]) => (
              <div key={yr} className="mb-3">
                <div className="label-uppercase !text-[10px]">{yr.replace("_", " ")}</div>
                <ul className="text-sm mt-1 ml-4 list-disc">{(Array.isArray(mods) ? mods : [mods]).map((m) => <li key={m}>{m}</li>)}</ul>
              </div>
            ))}
            <div className="mt-3 grid md:grid-cols-2 gap-x-6 text-sm">
              <div><b>Optional modules:</b> {d.curriculum.optional_modules_count ?? "—"}</div>
              <div><b>Specialisms:</b> {(d.curriculum.specialisms || []).join(", ") || "—"}</div>
              <div><b>Dissertation:</b> {d.curriculum.dissertation === true ? "Yes" : d.curriculum.dissertation === false ? "No" : "—"}</div>
              <div><b>Weekly workload:</b> {d.curriculum.weekly_workload_hours != null ? `${d.curriculum.weekly_workload_hours}h` : "—"}</div>
              <div><b>Flexibility:</b> {d.curriculum.flexibility || "—"}</div>
              {d.curriculum.programming_languages?.length ? <div><b>Languages/software:</b> {d.curriculum.programming_languages.join(", ")}</div> : null}
            </div>
            {d.curriculum.assessment_mix && (
              <div className="mt-3 text-sm"><b>Assessment mix:</b>{" "}
                {Object.entries(d.curriculum.assessment_mix).map(([k, v]) => `${k.replace("_pct","")}: ${v}%`).join(" · ")}
              </div>
            )}
          </>
        ) : d.curriculum?.specialisms?.length ? (
          <div className="text-sm">
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-1.5">
              <div><b>Specialisms:</b> {(d.curriculum.specialisms || []).join(", ")}</div>
            </div>
            <div className="mt-3 text-xs text-stone-500">
              Detailed module list, workload and assessment mix haven't been
              verified for this programme yet. Check the university's own
              course page.
            </div>
          </div>
        ) : isHesa ? (
          <HesaCurriculumSnapshot hesa={d.hesa_stats} />
        ) : isDaad ? (
          <div className="text-sm text-stone-700" data-testid="daad-curriculum-note">
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-1.5">
              {d.daad_stats?.programme_duration_text && (
                <div><b>Programme structure:</b> {d.daad_stats.programme_duration_text}</div>
              )}
              {d.daad_stats?.languages?.length > 0 && (
                <div><b>Language(s) of instruction:</b> {d.daad_stats.languages.join(" + ")}</div>
              )}
              <div>
                <b>Delivery:</b>{" "}
                {d.daad_stats?.is_online_possible === true
                  ? "On-campus (complete online option available)"
                  : "On-campus"}
              </div>
              {d.subject_area && d.subject_area !== "Other" && (
                <div><b>Discipline area:</b> {d.subject_area}</div>
              )}
            </div>
            <div className="mt-3 text-xs text-stone-500">
              Full module list is published on the university's course page.
              {d.source_provenance?.url && (
                <>
                  {" "}
                  <a
                    href={d.source_provenance.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#4C1D95] hover:underline inline-flex items-center gap-1"
                  >
                    See on DAAD <ExternalLink className="h-3 w-3" />
                  </a>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-stone-500 italic">Curriculum not yet verified for this degree.</div>
        )}
        <Ev ev={d.curriculum?.evidence} />
      </Section>

      <Section id="teaching" title="Teaching & learning">
        {/* Feb 19, 2026 · Chart cluster relocated here from the top of
            Cluster 1 so the four teaching-quality charts (Assessment
            Mix donut, TEF Gold, NSS Student Voice bars, Weekly Rhythm
            contact hours) render inside their own "Teaching & learning"
            semantic header. Legacy TefBadge/NssThemesBlock/DataRows
            below still render for non-UK degrees where the cluster
            self-hides. */}
        <TeachingLearningCluster degree={d} variant="teaching" />
        {/* Legacy TEF/NSS pills — only render when the richer cluster
            cards above wouldn't already cover the same signal, so UK
            degrees don't display duplicate TEF Gold badges or NSS
            themes twice. */}
        {!d.tef?.overall ? <TefBadge tef={d.hesa_stats?.tef} /> : null}
        {!d.nss_subscales ? <NssThemesBlock nss={d.hesa_stats?.nss_themes} /> : null}
        {d.teaching?.contact_hours_week ? (
          <div className="grid md:grid-cols-2 gap-x-8">
            <DataRow k="Contact hours/week" v={d.teaching.contact_hours_week} />
            <DataRow k="Lecture/seminar/tutorial/lab" v={`${d.teaching.lecture_hours_week}/${d.teaching.seminar_hours_week}/${d.teaching.tutorial_hours_week}/${d.teaching.lab_hours_week || 0}`} />
            <DataRow k="Typical class size" v={d.teaching.typical_class_size} />
            <DataRow k="Student:faculty ratio" v={`${d.teaching.student_faculty_ratio}:1`} />
            <DataRow k="% permanent faculty" v={`${d.teaching.permanent_faculty_pct}%`} />
            <DataRow k="Personal tutor" v={d.teaching.personal_tutor ? "Yes" : "No"} />
          </div>
        ) : isDaad ? (
          <div className="text-sm text-stone-700" data-testid="daad-teaching-note">
            Teaching hours, class sizes and assessment mix are not
            centrally published in Germany — each faculty publishes its own
            <em> Modulhandbuch</em> (module handbook). Expect roughly 20-24
            contact hours per week for a full-time bachelor's programme
            (lectures + Übungen + labs), with Prüfungen (exams) at the end
            of each semester.
            {d.source_provenance?.url && (
              <>
                {" "}
                <a
                  href={d.source_provenance.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#4C1D95] hover:underline inline-flex items-center gap-1"
                >
                  Check delivery on DAAD <ExternalLink className="h-3 w-3" />
                </a>
              </>
            )}
          </div>
        ) : (
          <div className="text-sm text-stone-500 italic">Teaching data not yet verified.</div>
        )}
        {isScorecard && <UsInstitutionSignals degree={d} uni={uni} />}
        <Ev ev={d.teaching?.evidence} />
      </Section>

      <Section id="accreditation" title="Accreditation & recognition">
        {(d.accreditation || []).length === 0 && (d.hesa_stats?.accreditations || []).length === 0 ? (
          isDaad ? (
            <Card className="p-4 border-emerald-300 bg-emerald-50/40" data-testid="daad-accreditation-note">
              <div className="font-editorial text-lg">German Accreditation Council (Akkreditierungsrat)</div>
              <div className="text-sm text-stone-700 mt-1">
                Every state-recognised German bachelor's programme is either
                system-accredited or programme-accredited through the{" "}
                <a href="https://www.akkreditierungsrat.de/en" target="_blank" rel="noreferrer" className="text-[#4C1D95] hover:underline">
                  Akkreditierungsrat
                </a>{" "}
                and listed in the{" "}
                <a href="https://anabin.kmk.org/anabin.html" target="_blank" rel="noreferrer" className="text-[#4C1D95] hover:underline">
                  ANABIN
                </a>{" "}
                database — a prerequisite for foreign-degree recognition
                in Germany. Public universities (Staatliche Hochschulen)
                additionally hold university-status recognition under
                the Hochschulrahmengesetz.
              </div>
              <div className="text-xs text-stone-500 mt-2 font-mono">
                Source: KMK / Akkreditierungsrat · verified{" "}
                {d.source_provenance?.date_checked || ""}
              </div>
            </Card>
          ) : isScorecard ? (
            <Card className="p-4 border-emerald-300 bg-emerald-50/40" data-testid="us-accreditation-note">
              <div className="font-editorial text-lg">US Regional Accreditation + SEVP</div>
              <div className="text-sm text-stone-700 mt-1">
                Every institution on College Scorecard is accredited by one
                of the seven US regional bodies (Middle States, New England,
                Higher Learning, Northwest, Southern, WASC-Sr, WASC-Jr) and
                certified for international students by{" "}
                <a href="https://www.ice.gov/sevis" target="_blank" rel="noreferrer" className="text-[#4C1D95] hover:underline">
                  SEVP
                </a>{" "}
                — the prerequisites for a valid F-1 student visa. Engineering
                programmes should additionally verify{" "}
                <a href="https://www.abet.org/accreditation/find-programs/" target="_blank" rel="noreferrer" className="text-[#4C1D95] hover:underline">
                  ABET
                </a>{" "}
                accreditation; business at{" "}
                <a href="https://www.aacsb.edu/accreditation" target="_blank" rel="noreferrer" className="text-[#4C1D95] hover:underline">
                  AACSB
                </a>; nursing at{" "}
                <a href="https://www.ccneaccreditation.org/" target="_blank" rel="noreferrer" className="text-[#4C1D95] hover:underline">
                  CCNE
                </a>. Confirm the specific programme accreditation on the
                university's own site before applying.
              </div>
              <div className="text-xs text-stone-500 mt-2 font-mono">
                Source: US Dept of Education Scorecard + DHS SEVP · verified{" "}
                {d.source_provenance?.date_checked || ""}
              </div>
            </Card>
          ) : (
            <div className="text-sm text-stone-500">No professional accreditation encoded. For humanities/social-science degrees this is normal; for medicine/engineering/law you should verify before applying.</div>
          )
        ) : (
          d.accreditation?.map((a, i) => (
            <Card key={i} className="p-4 border-stone-200 mb-2">
              <div className="font-editorial text-lg">{a.body}</div>
              <div className="text-sm text-stone-700 mt-1">Status: {a.status} · Granted {a.granted}{a.expires ? ` · Expires ${a.expires}` : ""}</div>
              <div className="text-sm mt-1"><b>Professional exemption:</b> {a.professional_exemption}</div>
              <div className="text-sm mt-1"><b>Recognised in:</b> {(a.licensure_eligible_in || []).join(", ")}</div>
              {a.conditions && <div className="text-sm mt-1"><b>Conditions:</b> {a.conditions}</div>}
              <Ev ev={a.evidence} />
            </Card>
          ))
        )}
        <HesaAccreditations accs={d.hesa_stats?.accreditations} />
      </Section>
      </ClusterMember>
    </>
  );
}
